import { toTenantDate } from '@utils/timeZone';
import { useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ChevronDown, Search, X } from 'lucide-react';
import { Popover, PopoverAnchor, PopoverContent } from '@components/ui/popover';
import { Checkbox } from '@components/ui/checkbox';
import { Avatar } from '@components/avatar';
import { AppTooltip } from '@components/ui/tooltip/tooltip';
import { SvgConsumer } from '@components/icons/svgConsumer';
import { useSchedulerSidebar } from '@contexts/scheduler/sidebar';
import { useDropdownsContext } from '@contexts/dropdowns';
import { useAccess } from '@hooks/access';
import { cn } from '@utils/cn';
import { filterOptions, sortPinnedFirst } from './helpers';
import { SchedulerSidebarEquipmentMultiSelectProps } from './types';
import { formatTime } from '@utils/i18n';

export const SchedulerSidebarEquipmentMultiSelect = ({
  disabled,
}: SchedulerSidebarEquipmentMultiSelectProps) => {
  const { t } = useTranslation('app', { keyPrefix: 'schedule.equipment' });
  const { t: tMultiSelect } = useTranslation('app', { keyPrefix: 'forms.default.multiSelect' });
  const { t: tActions } = useTranslation('app', { keyPrefix: 'actions' });
  const { equipmentMembers, setState, conflictsByAssetId } = useSchedulerSidebar();
  const access = useAccess();
  const popoverRef = useRef<HTMLDivElement>(null);
  const searchRef = useRef<HTMLInputElement>(null);
  const anchorRef = useRef<HTMLDivElement>(null);
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [pinnedAssetIds, setPinnedAssetIds] = useState<string[]>([]);

  const {
    assetMembers: { options: assetMembersOptions, disabled: assetMembersDisabled },
  } = useDropdownsContext();

  const availableAssets = useMemo(
    () => assetMembersOptions.filter((el) => el.id !== 'unassigned'),
    [assetMembersOptions],
  );

  const options = useMemo(
    () =>
      availableAssets.map((option) => ({
        value: option.id,
        label: `${option.name}${option.internalId ? ` (${option.internalId})` : ''}`,
        conflicts: conflictsByAssetId[option.id] || [],
      })),
    [availableAssets, conflictsByAssetId],
  );

  const selectedAssetIds = equipmentMembers
    .filter((el) => !!el.assetId && !el.discardedAt)
    .map((el) => el.assetId);

  const visibleOptions = sortPinnedFirst(filterOptions(options, search), pinnedAssetIds);

  const isSelected = (assetId: string) => selectedAssetIds.includes(assetId);

  const addMember = (assetId: string) => {
    const selected = availableAssets.find((el) => el.id === assetId);

    setState((prev) => ({
      ...prev,
      defaultEquipment: false,
      equipmentMembers: [
        ...prev.equipmentMembers,
        {
          id: undefined,
          assetId,
          name: selected?.name || '',
          internalId: selected?.internalId || '',
          conflicts: [],
          edit: false,
        },
      ],
    }));
  };

  const removeMember = (assetId: string) => {
    setState((prev) => ({
      ...prev,
      defaultEquipment: false,
      equipmentMembers: prev.equipmentMembers.filter((el) => el.assetId !== assetId),
    }));
  };

  const toggleMember = (assetId: string) => {
    if (isSelected(assetId)) removeMember(assetId);
    else addMember(assetId);
  };

  const isDisabled = disabled || access.readOnly || assetMembersDisabled || !options.length;

  const handleOpenChange = (next: boolean) => {
    setOpen(next);
    if (next) setPinnedAssetIds(selectedAssetIds);
    else setSearch('');
  };

  return (
    <div className="shrink-0">
      <div ref={popoverRef} className="overflow-visible" />
      <Popover open={open} onOpenChange={handleOpenChange}>
        <PopoverAnchor asChild>
          <div
            ref={anchorRef}
            className={cn(
              'flex h-7 w-full items-center gap-2 rounded border border-neutral-300 bg-white px-2.5 text-xs text-neutral-800',
              open && 'border-primary-600 outline outline-2 outline-primary-200',
              isDisabled && 'border-neutral-200 bg-neutral-50 text-neutral-400',
            )}>
            {open ? (
              <>
                <Search className="h-3.5 w-3.5 shrink-0 text-neutral-400" />
                <input
                  autoFocus
                  ref={searchRef}
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Escape') handleOpenChange(false);
                  }}
                  placeholder={t('searchPlaceholder')}
                  className="w-full bg-transparent text-xs text-neutral-800 outline-none placeholder:text-neutral-400"
                />
                {!!search && (
                  <button
                    type="button"
                    aria-label={tMultiSelect('clear')}
                    className="shrink-0 text-neutral-400 hover:text-neutral-600"
                    onMouseDown={(e) => e.preventDefault()}
                    onClick={() => {
                      setSearch('');
                      searchRef.current?.focus();
                    }}>
                    <X className="h-3.5 w-3.5" />
                  </button>
                )}
              </>
            ) : (
              <button
                type="button"
                disabled={isDisabled}
                onClick={() => handleOpenChange(true)}
                className="flex w-full items-center justify-between gap-2">
                <span className="flex items-center gap-1 font-normal text-neutral-400">
                  <SvgConsumer id="plus" className="shrink-0" />
                  <span>{t('add')}</span>
                </span>
                <ChevronDown className="h-3.5 w-3.5 shrink-0 text-neutral-400" />
              </button>
            )}
          </div>
        </PopoverAnchor>
        <PopoverContent
          align="start"
          side="bottom"
          container={popoverRef.current}
          onOpenAutoFocus={(event) => event.preventDefault()}
          onInteractOutside={(event) => {
            const target = event.target as Node | null;
            if (!target?.isConnected || anchorRef.current?.contains(target)) event.preventDefault();
          }}
          className="w-[var(--radix-popover-trigger-width)] p-0">
          <div className="scrollbar-thin max-h-[160px] overflow-y-auto py-1">
            {visibleOptions.length ? (
              visibleOptions.map((option) => (
                <button
                  key={option.value}
                  type="button"
                  onMouseDown={(e) => e.preventDefault()}
                  onClick={() => toggleMember(option.value)}
                  className={cn(
                    'flex w-full items-center gap-2 px-2.5 py-1.5 text-left text-xs',
                    isSelected(option.value) ? 'bg-primary-50' : 'hover:bg-primary-100',
                  )}>
                  <Checkbox checked={isSelected(option.value)} />
                  <Avatar
                    name={option.label}
                    hideTooltip
                    noOfInitials={1}
                    className="h-5 w-5 text-[10px]"
                  />
                  <span className="truncate font-medium text-neutral-600">{option.label}</span>
                  {!!option.conflicts.length && (
                    <AppTooltip
                      side="top"
                      className="max-w-none"
                      context={
                        <div className="flex flex-col">
                          <strong>{t('tooltip.conflict.title')}</strong>
                          {option.conflicts.map((el) => (
                            <span key={el.scheduleId}>
                              {[
                                el.projectIdentifier,
                                el.name,
                                formatTime(toTenantDate(el.startsAt)),
                                formatTime(toTenantDate(el.endsAt)),
                              ].join(' - ')}
                            </span>
                          ))}
                        </div>
                      }>
                      <span className="shrink-0">
                        <SvgConsumer id="warning" className="shrink-0 text-sm text-warning-500" />
                      </span>
                    </AppTooltip>
                  )}
                </button>
              ))
            ) : (
              <p className="px-2.5 py-2 text-xs text-neutral-400">{tMultiSelect('empty')}</p>
            )}
          </div>
          <div className="flex items-center justify-between gap-2 border-t px-2.5 py-1.5">
            <span className="text-[10px] text-neutral-400">{t('updateHint')}</span>
            <button
              type="button"
              className="cursor-pointer text-xs font-medium text-primary-600 hover:underline"
              onClick={() => handleOpenChange(false)}>
              {tActions('close').toLocaleUpperCase()}
            </button>
          </div>
        </PopoverContent>
      </Popover>
    </div>
  );
};
